import { useQuery } from '@tanstack/react-query';
import { PkButton, PkIcon } from '@uefadigital/panenka-react';
import { useState, useEffect, memo, useMemo, useRef, useCallback } from 'react';
import { Route, Routes, BrowserRouter as Router, useParams, useNavigate } from 'react-router';

import DefaultContent from './Content/DefaultContent';
import DisciplinaryBodiesContent from './Content/DisciplinaryBodiesContent';

import { slugSeparator } from '@cobalt/utilities';
import EventBusInstance from '@core/eventbus';
import { getEditorialByIdQuery } from '@queries/editorial';
import Loader from '@shared/Loader';
import Modal from '@shared/Modal';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { isEqual, mergeArrays } from '@utilities/collection-utils';

export const App = ({
  options: { containerSelector, deepLink, mode, enableDirectLink },
}: {
  options: {
    containerSelector: string;
    deepLink: boolean;
    mode: 'Default' | 'DisciplinaryBodies';
    enableDirectLink: boolean;
  };
}) => {
  const { id: sluggedId } = useParams();
  const urlId = useMemo(() => sluggedId?.split(slugSeparator)[0], [sluggedId]);
  const [currentId, setCurrentId] = useState(urlId);
  const [fromDeepLink, setFromDeepLink] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const loadedId = useRef(currentId);
  const navigate = useNavigate();

  const [prevId, setPrevId] = useState<string>(null);
  const [nextId, setNextId] = useState<string>(null);
  const { data: content } = useQuery({
    ...getEditorialByIdQuery(currentId),
    enabled: !!currentId,
  });

  const setVirtPath = useCallback((kind?: string) => {
    const pathName = window.location.pathname;
    const competitionTracking = window.competitionTracking;
    const pathParts = pathName.split('/').filter((part) => part !== '');

    if (pathParts[0] !== competitionTracking) {
      pathParts.unshift(competitionTracking);
    }

    const virtPath = `/${pathParts.join('/')}/`;

    const data = {
      virtPath: virtPath,
      pageName: virtPath,
      pageType: 'article',
      pageSubType: kind?.toLowerCase(),
    };

    GoogleTagManagerHelper.addPageMeta(data);
    GoogleTagManagerHelper.addVirtualPage(data);
  }, []);

  const setUrlId = useCallback(
    (id: string, replace?: boolean) => {
      const link = id ? `/${id}/` : '/';
      navigate(link, { replace });
    },
    [navigate],
  );

  useEffect(() => {
    if (!deepLink || currentId === loadedId.current) return;
    loadedId.current = currentId;
    setUrlId(currentId);
  }, [deepLink, currentId, setUrlId]);

  useEffect(() => {
    if (!loadedId.current || !content || !fromDeepLink) return;
    const kind = content?.classification.kind;
    setVirtPath(kind);
    setFromDeepLink(false);
  }, [content, content?.classification.kind, setVirtPath, fromDeepLink]);

  const handleClick = (newId: string) => () => {
    setFromDeepLink(true);
    setCurrentId(newId);
  };

  useEffect(() => {
    const getIdsFromDom = () => {
      const cards = Array.from(document.querySelectorAll<HTMLElement>(`${containerSelector ?? ''} [data-modal-id]`));
      const newIds = cards.map((el) => {
        el.classList.remove('with-modal--loading');
        const id = el.dataset.modalId;

        el.addEventListener('click', (e) => {
          if (!enableDirectLink) {
            e.preventDefault();
            setFromDeepLink(true);
            setCurrentId(id);
          }

          return false;
        });
        return id;
      });

      const mergedIds = mergeArrays(ids, newIds);
      !isEqual(mergedIds, ids) && setIds(mergedIds);
    };

    getIdsFromDom();
    EventBusInstance.on('lazyload-loaded', getIdsFromDom, this);

    return () => {
      EventBusInstance.off('lazyload-loaded', getIdsFromDom, this);
    };
  }, [containerSelector, enableDirectLink, ids, setCurrentId]);

  useEffect(() => {
    if (!ids?.length) return;

    const index = ids.findIndex((id) => id === urlId);
    setNextId(index >= 0 && index < ids.length ? ids[index + 1] : null);
    setPrevId(index >= 0 ? ids[index - 1] : null);

    if (index >= 0) {
      setCurrentId(ids[index]);
    }
  }, [deepLink, ids, setCurrentId, urlId]);

  useEffect(() => {
    const handleArrow = (evt: KeyboardEvent) => {
      const keyActions = {
        ArrowLeft: () => prevId && setCurrentId(prevId),
        ArrowRight: () => nextId && setCurrentId(nextId),
      } as const;

      keyActions[evt.key]?.();
    };

    document.addEventListener('keydown', handleArrow, false);
    return () => {
      document.removeEventListener('keydown', handleArrow, false);
    };
  }, [prevId, nextId, setCurrentId]);

  return (
    <Modal
      showModal={!!currentId}
      handleCloseModal={handleClick(null)}
      overlayClassName={mode === 'Default' ? 'disciplinary-upd-modal-overlay ' : 'disciplinary-cedb-modal-overlay '}
      className={mode === 'Default' ? 'disciplinary-upd-modal ' : 'disciplinary-cedb-modal '}
    >
      {!!prevId && (
        <PkButton className='prev-card' appearance='icon' onPkClick={handleClick(prevId)}>
          <PkIcon name='navigation-chevron-left' />
        </PkButton>
      )}
      <div className='disciplinary-modal__content'>
        {!content && <Loader />}
        {content && mode === 'Default' && <DefaultContent content={content} />}
        {content && mode === 'DisciplinaryBodies' && <DisciplinaryBodiesContent content={content} />}
      </div>
      {!!nextId && (
        <PkButton className='next-card' appearance='icon' onPkClick={handleClick(nextId)}>
          <PkIcon name='navigation-chevron-right' />
        </PkButton>
      )}
    </Modal>
  );
};

const RoutedApp = ({ options }) => (
  <Router basename={options.deepLink ? options.deepLinkBaseUrl : location.pathname}>
    <Routes>
      <Route path='/' element={<App options={options} />} />
      {options.deepLink && <Route path='/:id' element={<App options={options} />} />}
    </Routes>
  </Router>
);

export default memo(RoutedApp);
